import AuthShowcase from '@auth/components/AuthShowcase'
import {
  AuthFooter,
  AuthForm,
  AuthLanguageMenu,
  AuthPane,
  AuthPaneBody,
  AuthPaneHeader,
  AuthPrompt,
  AuthShell,
  AuthSubtitle,
  AuthTitle,
  BankyLogo,
  MMButton
} from '@components'
import cn from '@integrations/classnames'
import type * as React from '@integrations/react'
import { useEffect, useState } from '@integrations/react'
import { Link } from '@integrations/routing'
import { paths, useLoginQry } from 'mmfintech-backend-api'
import { tr } from 'mmfintech-commons'
import { ErrorDisplay, Input } from 'mmfintech-portal-commons'

import styles from './LoginView.module.scss'

type ZoomState = 'initial' | 'zoomed-in' | 'zooming-out' | 'zoomed-out'

function LoginView() {
  const { formValues, login, loginError, loginFetching, resetLogin } = useLoginQry()
  const [loginZoomState, setLoginZoomState] = useState<ZoomState>('initial')

  // Zoom in login form on initial load
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoginZoomState('zoomed-in')
    }, 50)
    return () => clearTimeout(timer)
  }, [])

  // Zoom the form out while signing in and back in once the request settles
  useEffect(() => {
    if (loginFetching) {
      setLoginZoomState('zoomed-out')
    } else if (loginZoomState === 'zoomed-out') {
      setLoginZoomState('zoomed-in')
    }
  }, [loginFetching, loginZoomState])

  async function handleSubmit(e: React.BaseSyntheticEvent) {
    e.preventDefault()

    // Validate before starting animation
    if (!formValues.areValid()) {
      return
    }

    setLoginZoomState('zooming-out')

    // Wait for zoom out animation to start, then submit
    setTimeout(() => {
      void login()
    }, 300)
  }

  return (
    <AuthShell layout='split' className={cn('login-wrapper')}>
      <AuthPane className={cn('login-container')}>
        <AuthPaneHeader>
          <BankyLogo />
          <AuthLanguageMenu bordered placement='inline' />
        </AuthPaneHeader>

        <AuthPaneBody>
          <div className={cn(styles['login-main-section'], 'login-main-section')}>
            <div
              className={cn(styles['login-loader'], 'login-loader', {
                [styles['is-visible']]: loginZoomState === 'zooming-out' || loginZoomState === 'zoomed-out',
                'is-visible': loginZoomState === 'zooming-out' || loginZoomState === 'zoomed-out'
              })}
            >
              <div className={cn(styles['spinner'], 'spinner')} />
            </div>

            <div
              className={cn(styles['login-form-section'], 'login-form-section', 'login-content')}
              data-zoom-state={loginZoomState}
            >
              <AuthTitle variant='display' className={cn('title')}>
                {tr('FRONTEND.LOGIN.WELCOME_TITLE', 'Welcome back')}
              </AuthTitle>
              <AuthSubtitle className={cn('subtitle')}>
                {tr('FRONTEND.LOGIN.WELCOME_SUBTITLE', 'Sign in to your Banky merchant account.')}
              </AuthSubtitle>

              <AuthForm className={cn('login-form')} noValidate onSubmit={handleSubmit}>
                <Input
                  type='email'
                  data-test='email'
                  label={tr('FRONTEND.LOGIN.EMAIL', 'Email')}
                  {...formValues.registerInput('email', resetLogin)}
                  required={true}
                  hideRequired={false}
                  disabled={loginFetching}
                  autoFocus
                />

                <Input
                  type='password'
                  data-test='password'
                  label={tr('FRONTEND.LOGIN.PASSWORD', 'Password')}
                  {...formValues.registerInput('password', resetLogin)}
                  required={true}
                  hideRequired={false}
                  disabled={loginFetching}
                  autoComplete='off'
                  hideErrorLine
                />

                <div className={cn(styles['login-link'], 'login-link')} data-test='forgot-password'>
                  <Link to={paths.forgotPassword()}>{tr('FRONTEND.LOGIN.FORGOT_PASSWORD', 'Forgot password?')}</Link>
                </div>

                <ErrorDisplay error={loginError} />

                <MMButton
                  type='submit'
                  caption={tr('FRONTEND.LOGIN.BUTTON_LOGIN', 'Sign in')}
                  loading={loginFetching}
                  size='large'
                  fullWidth
                  data-test='button-submit'
                />

                <AuthPrompt className={cn(styles['login-signup'], 'login-signup')}>
                  {tr('FRONTEND.LOGIN.NO_ACCOUNT', "Don't have an account?")}{' '}
                  <Link to={paths.signUp()} data-test='sign-up'>
                    <span>{tr('FRONTEND.LOGIN.SIGN_UP_FREE', 'Sign up for FREE')}</span>
                  </Link>
                </AuthPrompt>
              </AuthForm>
            </div>
          </div>
        </AuthPaneBody>

        <AuthFooter />
      </AuthPane>

      <AuthShowcase />
    </AuthShell>
  )
}

export default LoginView
